// src/api.ts
import {
  makeAuthHeaders,
  makeUrl,
  RustyDeposit,
  RustyReferral,
  RustySubscription
} from "@gofranz/common";
import axios from "axios";
var GptMuseApi = class {
  constructor({ baseUrl, timeout, auth, errorHandler, successHandler }) {
    this.getAuthApi = () => {
      if (!this.auth) {
        throw new Error("Auth config not set");
      }
      return this.auth;
    };
    this.getDepositApi = () => new RustyDeposit({ client: this.client });
    this.getSubscriptionApi = () => new RustySubscription({ client: this.client });
    this.getReferralApi = () => new RustyReferral({ client: this.client });
    this.getAccessToken = () => this.getAuthApi().getAccessToken();
    this.getAuthHeaders = () => makeAuthHeaders(this.getAccessToken());
    this.getAxiosConfig = () => ({ headers: this.getAuthHeaders(), timeout: this.timeout });
    this.getAccountBalance = async () => this.getDepositApi().getAccountBalance(this.getAccessToken());
    this.getAccountMovements = async (query = {}) => this.getDepositApi().getAccountMovements(query, this.getAccessToken());
    this.getDeposits = async (query) => this.getDepositApi().getDeposits(query, this.getAccessToken());
    this.getDeposit = async (id) => this.getDepositApi().getDeposit(id, this.getAccessToken());
    this.newDeposit = async (deposit) => this.getDepositApi().newDeposit(deposit, this.getAccessToken());
    // Subscription endpoints
    this.getSubscriptionPlans = async () => this.getSubscriptionApi().getSubscriptionPlans();
    this.getCurrentSubscription = async () => this.getSubscriptionApi().getCurrentSubscription(this.getAccessToken());
    this.subscribeToplan = async (planId) => this.getSubscriptionApi().subscribeToplan(planId, this.getAccessToken());
    this.cancelSubscription = async () => this.getSubscriptionApi().cancelSubscription(this.getAccessToken());
    this.getSubscriptionUsage = async () => this.getSubscriptionApi().getSubscriptionUsage(this.getAccessToken());
    this.createCustomerPortalSession = async () => this.getSubscriptionApi().createCustomerPortalSession(this.getAccessToken());
    // Referral endpoints
    this.getReferralCode = async () => this.getReferralApi().getReferralCode(this.getAccessToken());
    this.getReferralStats = async () => this.getReferralApi().getReferralStats(this.getAccessToken());
    this.getReferralHistory = async () => this.getReferralApi().getReferralHistory(this.getAccessToken());
    // Email verification endpoints
    this.newVerifiedEmail = async (data) => {
      await this.client.post("/a/emails/verify", data, this.getAxiosConfig());
    };
    this.verifyVerifiedEmail = async (id) => {
      await this.client.post(
        "/a/emails/verify/retry",
        { verified_email_id: id }
      );
    };
    this.deleteVerifiedEmail = async (id) => {
      await this.client.delete(`/a/emails/${id}`, this.getAxiosConfig());
    };
    this.getVerifiedEmails = async () => {
      const response = await this.client.get(
        "/a/emails"
      );
      return response.data;
    };
    // Chat completions endpoints
    this.chatCompletions = async (data) => {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream"
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          throw new Error(`Chat completion failed: ${response.statusText}`);
        }
        if (errorData.error_code) {
          throw new Error(JSON.stringify(errorData));
        }
        const errorMessage = errorData.message || errorData.error || `Chat completion failed: ${response.statusText}`;
        throw new Error(errorMessage);
      }
      return {
        stream: response.body,
        headers: response.headers
      };
    };
    this.chatCompletionsAuthenticated = async (data) => {
      const response = await fetch(`${this.baseUrl}/a/chat/completions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
          "Authorization": `Bearer ${this.getAccessToken()}`
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          throw new Error(`Authenticated chat completion failed: ${response.statusText}`);
        }
        if (errorData.error_code) {
          throw new Error(JSON.stringify(errorData));
        }
        const errorMessage = errorData.message || errorData.error || `Authenticated chat completion failed: ${response.statusText}`;
        throw new Error(errorMessage);
      }
      return {
        stream: response.body,
        headers: response.headers
      };
    };
    // Models endpoints
    this.listModels = async (query) => {
      const response = await this.client.get(
        makeUrl("/models", query)
      );
      return response.data;
    };
    this.listProvidersForModel = async (modelId, query) => {
      const response = await this.client.get(
        makeUrl(`/models/${modelId}/providers`, query)
      );
      return response.data;
    };
    // Rate limit endpoints
    this.getRateLimitStatus = async () => {
      const response = await fetch(`${this.baseUrl}/rate-limit/status`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (!response.ok) {
        throw new Error(`Failed to get rate limit status: ${response.statusText}`);
      }
      return await response.json();
    };
    // Characters endpoints
    this.listCharacters = async () => {
      const response = await this.client.get(
        makeUrl("/characters")
      );
      return response.data;
    };
    // Two-Factor Authentication endpoints
    this.getTwoFactorStatus = async () => {
      const response = await this.client.get("/a/2fa/status");
      return response.data;
    };
    this.setupTotp = async (name) => {
      const response = await this.client.post(
        "/a/2fa/setup/totp",
        { name: name || "Authenticator" }
      );
      return response.data;
    };
    this.verifyTotpSetup = async (setupToken, code) => {
      const response = await this.client.post(
        "/a/2fa/setup/totp/verify",
        { setup_token: setupToken, code }
      );
      return response.data;
    };
    this.disableTwoFactorMethod = async (methodId, code) => {
      await this.client.delete(`/a/2fa/methods/${methodId}`, {
        ...this.getAxiosConfig(),
        data: { code }
      });
    };
    this.getRecoveryCodesCount = async () => {
      const response = await this.client.get("/a/2fa/recovery-codes");
      return response.data;
    };
    this.regenerateRecoveryCodes = async (code) => {
      const response = await this.client.post(
        "/a/2fa/recovery-codes/regenerate",
        { code }
      );
      return response.data;
    };
    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else {
      this.baseUrl = "https://api.gpt-muse.com/v1";
    }
    console.info(`[api] => baseUrl: ${this.baseUrl}`);
    if (timeout) {
      this.timeout = timeout;
    } else {
      this.timeout = 5e3;
    }
    if (auth) {
      this.auth = auth;
    } else {
      console.warn("Auth config not set");
    }
    this.errorHandler = errorHandler;
    this.successHandler = successHandler;
    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: this.timeout,
      headers: {
        "Content-Type": "application/json"
      }
    });
    this.client.interceptors.request.use(async (config) => {
      const needsAuth = config.url?.startsWith("/a/");
      if (needsAuth && this.auth) {
        if (!this.auth.hasValidAccessToken()) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        const token = this.auth.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    });
    this.client.interceptors.response.use(
      (response) => {
        if (this.successHandler) {
          this.successHandler(response);
        }
        return response;
      },
      // Pass through successful responses
      (error) => {
        if (this.errorHandler) {
          this.errorHandler(error);
        }
        return Promise.reject(error);
      }
    );
  }
};
export {
  GptMuseApi
};
//# sourceMappingURL=index.js.map